import { clientLogger } from '@/lib/clientLogger';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { ContactCreateDTO, ContactDTO, ContactUpdateDTO, JobContactAssignmentDTO, JobContactInputDTO, JobDTO } from '@/types';
import { ContactFormModal } from './ContactFormModal';
import { ContactListItem } from './ContactList';
import { ContactSearchModal } from './ContactSearchModal';
import { Plus, Users, MessageSquare, Phone, ChevronDown, Contact } from 'lucide-react';
import { useNotifications, notificationService } from '../ui/NotificationSystem';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { destructiveCopy } from '@/lib/destructiveConfirmMessages';
import clsx from 'clsx';
import { formatPhoneNumber } from '@/lib/phoneUtils';

interface JobContactsSectionProps {
  job: JobDTO;
  onJobUpdated?: (job: JobDTO) => void;
  className?: string;
  onSmsClick?: (contact: ContactDTO, which: 'primary' | 'additional') => void;
  /** Place a call to the contact. Omit to hide the call button. */
  onCallClick?: (contact: ContactDTO, which: 'primary' | 'additional') => void;
  onPastJobSelect?: (jobId: string) => void;
  /** Open the job's Contact Info modal on this contact. Omit to hide the button. */
  onContactInfoClick?: (contact: ContactDTO) => void;
  /** When true, hides add/edit/delete contact actions */
  disabled?: boolean;
}

type ContactFormMode = 'create' | 'edit';

export function JobContactsSection({ job, onJobUpdated, className, onSmsClick, onCallClick, onPastJobSelect, onContactInfoClick, disabled = false }: JobContactsSectionProps) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();


  const [isContactFormOpen, setContactFormOpen] = useState(false);
  const [contactFormMode, setContactFormMode] = useState<ContactFormMode>('create');
  const [selectedAssignment, setSelectedAssignment] = useState<JobContactAssignmentDTO | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearchModalOpen, setSearchModalOpen] = useState(false);
  const [assignmentNotesSnapshot, setAssignmentNotesSnapshot] = useState('');
  const [assignmentPendingDetach, setAssignmentPendingDetach] = useState<JobContactAssignmentDTO | null>(null);
  /** Contact whose primary/additional call picker is open (only for contacts with two numbers). */
  const [callMenuContactId, setCallMenuContactId] = useState<string | null>(null);

  const attachContactMutation = useMutation({
    mutationFn: async (payload: JobContactInputDTO) => {
      return apiService.attachContactToJob(job.id, payload);
    },
    onSuccess: async () => {
      await refreshJobDetails();
      addNotification(notificationService.success('Contact added to job'));
    },
    onError: (error: any) => {
      clientLogger.error('Failed to attach contact', error);
      addNotification(notificationService.error('Failed to add contact', error.message ?? 'Please try again.'));
    },
  });

  const updateJobContactMutation = useMutation({
    mutationFn: async ({ contactId, payload }: { contactId: string; payload: JobContactInputDTO }) => {
      return apiService.updateJobContact(job.id, contactId, payload);
    },
    onSuccess: async () => {
      await refreshJobDetails();
      addNotification(notificationService.success('Contact updated'));
    },
    onError: (error: any) => {
      clientLogger.error('Failed to update job contact', error);
      addNotification(notificationService.error('Failed to update contact', error.message ?? 'Please try again.'));
    },
  });

  const detachContactMutation = useMutation({
    mutationFn: async (contactId: string) => apiService.detachContactFromJob(job.id, contactId),
    onSuccess: async () => {
      await refreshJobDetails();
      addNotification(notificationService.success('Contact removed from job'));
    },
    onError: (error: any) => {
      clientLogger.error('Failed to remove job contact', error);
      addNotification(notificationService.error('Failed to remove contact', error.message ?? 'Please try again.'));
    },
  });

  const refreshJobDetails = async () => {
    try {
      const updatedJob = await apiService.getJobById(job.id);
      // Update both the list cache and single-job cache directly (no extra refetch)
      queryClient.setQueriesData({ queryKey: ['jobs'] }, (existing: any) => {
        if (!existing) return existing;
        const isArray = Array.isArray(existing);
        const items = isArray ? existing : existing.jobs;
        if (!Array.isArray(items)) return existing;
        const updatedItems = items.map((j: JobDTO) => (j.id === updatedJob.id ? updatedJob : j));
        if (isArray) return updatedItems;
        return { ...existing, jobs: updatedItems };
      });
      queryClient.setQueryData(['job', updatedJob.id], updatedJob);
      if (onJobUpdated) {
        onJobUpdated(updatedJob);
      }
    } catch (error) {
      clientLogger.error('Failed to refresh job data', error);
    }
  };

  const handleCreateNewContact = () => {
    setSelectedAssignment(null);
    setAssignmentNotesSnapshot('');
    setContactFormMode('create');
    setContactFormOpen(true);
  };

  const handleSelectExistingContact = () => {
    setSearchModalOpen(true);
  };

  const handleContactResolution = async (resolution: import('./ContactFormModal').ContactResolution) => {
    try {
      setIsSubmitting(true);

      switch (resolution.action) {
        case 'create': {
          const createPayload = {
            name: resolution.contact.name,
            phone: resolution.contact.phone,
            additional_phone: resolution.contact.additional_phone,
            email: resolution.contact.email,
            notes: resolution.contact.notes,
          };
          const createdContact = await apiService.createContact(createPayload);
          await attachContactMutation.mutateAsync({
            id: createdContact.id,
            notes: resolution.assignmentNotes,
          });
          break;
        }
        case 'useExisting': {
          await attachContactMutation.mutateAsync({
            id: resolution.contactId,
            notes: resolution.assignmentNotes,
          });
          break;
        }
        case 'createNew': {
          // This is the "edit shared contact -> create new instead" flow
          if (selectedAssignment) {
            const contactId = selectedAssignment.id || selectedAssignment.contact.id;
            // Detach old
            await detachContactMutation.mutateAsync(contactId);

            // Create new
            const createPayload = {
              name: resolution.contact.name,
              phone: resolution.contact.phone,
              additional_phone: resolution.contact.additional_phone,
              email: resolution.contact.email,
              notes: resolution.contact.notes,
            };
            const createdContact = await apiService.createContact(createPayload);

            // Attach new
            await attachContactMutation.mutateAsync({
              id: createdContact.id,
              notes: resolution.assignmentNotes,
            });
          }
          break;
        }
      }
      setContactFormOpen(false);
    } catch (error: any) {
      clientLogger.error('Contact resolution failed', error);
      addNotification(
        notificationService.error('Failed to save contact', error.message ?? 'Please try again.')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSubmit = async ({
    contact,
    assignmentNotes,
  }: {
    contact: ContactCreateDTO | ContactUpdateDTO;
    assignmentNotes?: string;
  }) => {
    try {
      setIsSubmitting(true);
      if (contactFormMode === 'create') {
        const createPayload: ContactCreateDTO = {
          name: contact.name ?? '',
          phone: contact.phone ?? '',
          additional_phone: contact.additional_phone,
          email: contact.email,
          notes: contact.notes,
        };
        const createdContact: ContactDTO = await apiService.createContact(createPayload);
        await attachContactMutation.mutateAsync({
          id: createdContact.id,
          notes: assignmentNotes,
        });
      } else if (contactFormMode === 'edit' && selectedAssignment) {
        // Use contact_id if available, otherwise fall back to contact.id
        const contactId = selectedAssignment.id || selectedAssignment.contact.id;
        if (!contactId) {
          throw new Error('Contact ID is required for editing');
        }
        await apiService.updateContact(contactId, contact as ContactUpdateDTO);
        await updateJobContactMutation.mutateAsync({
          contactId: contactId,
          payload: { notes: assignmentNotes },
        });
      }
      setContactFormOpen(false);
    } catch (error: any) {
      clientLogger.error('Contact form submission failed', error);
      addNotification(
        notificationService.error('Failed to save contact', error.message ?? 'Please try again.')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExistingContactSelect = async (contact: ContactDTO) => {
    setSearchModalOpen(false);
    try {
      await attachContactMutation.mutateAsync({
        id: contact.id,
      });
    } catch {
      // Error handling already performed in mutation onError
    }
  };

  // Create a map of contact ID to assignment for reliable lookups
  const assignmentMap = new Map<string, JobContactAssignmentDTO>();
  (job.contacts ?? []).forEach((assignment) => {
    assignmentMap.set(assignment.id, assignment);
    assignmentMap.set(assignment.contact.id, assignment);
  });

  const contactItems: ContactListItem[] = (job.contacts ?? []).map((assignment) => ({
    id: assignment.id || assignment.contact.id, // Use contact_id if available, otherwise fall back to contact.id
    contact: assignment.contact,
    notes: assignment.notes,
  }));

  const requestRemoveContact = (assignment: JobContactAssignmentDTO) => {
    setAssignmentPendingDetach(assignment);
  };

  const confirmRemoveContactFromJob = async () => {
    if (!assignmentPendingDetach) return;
    const assignment = assignmentPendingDetach;
    const contactId = assignment.id || assignment.contact.id;
    if (!contactId) {
      throw new Error('Contact ID is required for removal');
    }
    await detachContactMutation.mutateAsync(contactId);
  };



  return (
    <div className={clsx('space-y-2', className)}>
      {/* Header with buttons */}
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-1.5 text-sm font-medium text-gray-600">
          <Users className="h-4 w-4 text-indigo-500" />
          Contacts
        </label>
        {!disabled && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCreateNewContact}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-0.5"
            >
              <Plus className="h-3 w-3" />
              New
            </button>
            <button
              type="button"
              onClick={handleSelectExistingContact}
              className="text-xs text-primary-600 hover:text-gray-700 font-medium flex items-center gap-0.5"
            >
              <Users className="h-3 w-3" />
              Existing
            </button>
          </div>
        )}
      </div>

      {/* Contact list - always visible, compact style */}
      {contactItems.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-2 text-center">
          <p className="text-xs text-gray-500">No contacts added</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {contactItems.map((item) => {
            const assignment = assignmentMap.get(item.contact.id);
            return (
              <div key={item.contact.id} className="flex items-start justify-between px-2 py-1.5 rounded-lg text-sm bg-white border border-gray-200">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Users className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                    <span className="font-medium truncate text-gray-900 text-sm">{item.contact.name || 'No contact name'}</span>
                    {item.contact.phone && (
                      <span className="text-xs text-gray-500 truncate hidden sm:inline">
                        • {item.contact.phone_masked ? item.contact.phone : formatPhoneNumber(item.contact.phone)}
                        {item.contact.additional_phone
                          ? ` / ${item.contact.phone_masked ? item.contact.additional_phone : formatPhoneNumber(item.contact.additional_phone)}`
                          : ''}
                      </span>
                    )}
                  </div>
                  {item.notes && (
                    <p className="text-xs text-gray-500 mt-0.5 pl-5 italic whitespace-pre-wrap">"{item.notes}"</p>
                  )}
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  {onContactInfoClick && (
                    <button
                      type="button"
                      onClick={() => onContactInfoClick(item.contact)}
                      className="p-1.5 rounded-md text-amber-600 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                      title="Contact info"
                    >
                      <Contact className="h-5 w-5" />
                    </button>
                  )}
                  {/* Call Button — hidden for masked contacts (masked calling is mobile-app only, the
                      desktop softphone can't resolve an opaque contact reference). With two numbers it
                      offers a primary/additional picker, mirroring the mobile job screen. */}
                  {onCallClick && item.contact.phone && !item.contact.phone_masked && (
                    item.contact.additional_phone?.trim() ? (
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setCallMenuContactId((prev) => (prev === item.contact.id ? null : item.contact.id))}
                          className="flex items-center p-1.5 rounded-md text-green-600 hover:bg-green-50 hover:text-green-700 transition-colors"
                          title="Call"
                          aria-haspopup="listbox"
                          aria-expanded={callMenuContactId === item.contact.id}
                        >
                          <Phone className="h-5 w-5" />
                          <ChevronDown className={clsx('h-3.5 w-3.5 transition-transform', callMenuContactId === item.contact.id && 'rotate-180')} />
                        </button>
                        {callMenuContactId === item.contact.id && (
                          <>
                            {/* Click-catcher closes the picker */}
                            <div className="fixed inset-0 z-40" onClick={() => setCallMenuContactId(null)} />
                            <div className="absolute right-0 top-full z-50 mt-1 min-w-[11rem] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl" role="listbox">
                              <button
                                type="button"
                                className="block w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50"
                                onClick={() => { setCallMenuContactId(null); onCallClick(item.contact, 'primary'); }}
                              >
                                <span className="block text-xs text-gray-500">Primary</span>
                                <span className="block text-sm font-medium text-gray-900">{formatPhoneNumber(item.contact.phone)}</span>
                              </button>
                              <button
                                type="button"
                                className="block w-full px-3 py-2 text-left hover:bg-gray-50"
                                onClick={() => { setCallMenuContactId(null); onCallClick(item.contact, 'additional'); }}
                              >
                                <span className="block text-xs text-gray-500">Additional</span>
                                <span className="block text-sm font-medium text-gray-900">{formatPhoneNumber(item.contact.additional_phone)}</span>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onCallClick(item.contact, 'primary')}
                        className="p-1.5 rounded-md text-green-600 hover:bg-green-50 hover:text-green-700 transition-colors"
                        title="Call"
                      >
                        <Phone className="h-5 w-5" />
                      </button>
                    )
                  )}
                  {/* SMS Button */}
                  {onSmsClick && item.contact.phone && (
                    <button
                      type="button"
                      onClick={() => onSmsClick(item.contact, 'primary')}
                      className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      title="Send SMS"
                    >
                      <MessageSquare className="h-5 w-5" />
                    </button>
                  )}
                  {/* Masked job (technician on a CallMaskingEnabled job): the phone shown is the
                      last-4 placeholder, so editing would overwrite the real number. Same rule as
                      JobContactsMobileModal's canEditContacts. */}
                  {!disabled && !item.contact.phone_masked && (
                    <button
                      type="button"
                      onClick={() => {
                        if (assignment) {
                          setSelectedAssignment(assignment);
                          setAssignmentNotesSnapshot(assignment.notes || '');
                          setContactFormMode('edit');
                          setContactFormOpen(true);
                        }
                      }}
                      className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                      title="Edit contact"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  )}
                  {!disabled && assignment && (
                    <button
                      type="button"
                      onClick={() => requestRemoveContact(assignment)}
                      className="p-1.5 rounded-md text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                      title="Remove from job"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ContactFormModal
        isOpen={isContactFormOpen}
        onClose={() => setContactFormOpen(false)}
        onSubmit={handleContactSubmit}
        onResolution={handleContactResolution}
        onJobSelect={onPastJobSelect}
        companyId={job.company?.id}
        contact={contactFormMode === 'edit' ? selectedAssignment?.contact : undefined}
        title={contactFormMode === 'edit' ? 'Edit Contact' : 'New Contact'}
        confirmLabel={contactFormMode === 'edit' ? 'Save Changes' : 'Create Contact'}
        isSubmitting={isSubmitting || attachContactMutation.isPending || updateJobContactMutation.isPending}
        includeAssignmentNotes
        initialAssignmentNotes={assignmentNotesSnapshot}
      />

      <ContactSearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        onSelect={handleExistingContactSelect}
      />

      <ConfirmDialog
        isOpen={!!assignmentPendingDetach}
        onClose={() => setAssignmentPendingDetach(null)}
        onConfirm={confirmRemoveContactFromJob}
        isLoading={detachContactMutation.isPending}
        title="Remove contact from job?"
        message={
          <div className="space-y-2">
            <span>{destructiveCopy.removeJobContact}</span>
            <span className="block text-sm">{destructiveCopy.cannotUndo}</span>
          </div>
        }
        confirmText="Remove"
      />
    </div>
  );
}

